<script setup>
import { computed, onMounted, onUnmounted, ref, toRef, watch } from 'vue';
import QRCode from 'qrcode';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import NextButton from 'dashboard/components-next/button/Button.vue';
import WahaAPI from 'dashboard/api/waha';
import InboxesAPI from 'dashboard/api/inboxes';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  detectPasskeyExtension,
  signPasskeyChallenge,
  isChrome,
  EXTENSION_INSTALL_URL,
} from 'dashboard/helper/wahaPasskey';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const toDataUrl = async value => {
  if (!value) return null;
  const trimmed = value.trim();

  if (!trimmed) return null;

  if (trimmed.startsWith('data:image')) {
    return trimmed;
  }

  const compact = trimmed.replace(/\s+/g, '');

  const isBase64 = () => {
    try {
      return btoa(atob(compact)) === compact;
    } catch (error) {
      return false;
    }
  };

  if (isBase64()) {
    return `data:image/png;base64,${compact}`;
  }

  return QRCode.toDataURL(trimmed);
};

const normalizeQrPayload = async payload => {
  if (!payload) return null;

  if (typeof payload === 'string') {
    return toDataUrl(payload);
  }

  const candidate =
    payload.base64 ||
    payload.qrcode?.base64 ||
    payload.qrcode?.code ||
    payload.qrcode ||
    payload.code ||
    payload.value ||
    null;

  return toDataUrl(candidate);
};

const inbox = toRef(props, 'inbox');
const { t } = useI18n();

const isGeneratingQR = ref(false);
const isSessionAction = ref(false);
const isRecreating = ref(false);
const connectionStatus = ref(null);
const qrCode = ref(null);

// Passkey (WebAuthn): algumas contas exigem passkey ao parear, além do QR.
const passkeyExtension = ref({ available: false, browser: 'chrome' });
const passkeyState = ref('idle'); // idle | signing | done | error
const passkeyError = ref('');
const passkeyRequiredStatuses = new Set([
  'passkey_required',
  'passkey-required',
  'passkey required',
]);
const isPasskeyRequired = computed(() =>
  passkeyRequiredStatuses.has(connectionStatus.value || '')
);
const passkeyInstallUrl = computed(() =>
  isChrome() ? EXTENSION_INSTALL_URL.chrome : EXTENSION_INSTALL_URL.firefox
);
const openInstall = () =>
  window.open(passkeyInstallUrl.value, '_blank', 'noopener');

const providerConfig = computed(() => inbox.value.provider_config || {});

const sessionName = computed(() => {
  const config = providerConfig.value;
  return config.session_name || config.instance_name || '';
});

const connectedStatuses = new Set(['open', 'connected', 'working']);

const canShowQRCode = computed(() => {
  return !connectedStatuses.has(connectionStatus.value || '');
});

const isConnected = computed(() =>
  connectedStatuses.has(connectionStatus.value || '')
);

const statusText = computed(() => {
  if (isConnected.value) return t('INBOX_MGMT.ADD.WAHA.STATUS.CONNECTED');
  const status = (connectionStatus.value || '').toLowerCase();
  if (['connecting', 'starting'].includes(status)) {
    return t('INBOX_MGMT.ADD.WAHA.STATUS.CONNECTING');
  }
  return t('INBOX_MGMT.ADD.WAHA.STATUS.STOPPED');
});

const qrScanStatuses = new Set([
  'scan_qr_code',
  'scan-qr-code',
  'scan qr code',
]);

const loadConnectionStatus = async () => {
  try {
    const config = providerConfig.value;
    if (!config.api_url || !config.admin_token) {
      connectionStatus.value = null;
      return;
    }

    const sessions = await WahaAPI.listSessions(
      config.api_url,
      config.admin_token,
      { includeStopped: true }
    );

    const session = Array.isArray(sessions)
      ? sessions.find(item => {
          return (
            item.name === sessionName.value ||
            item.session === sessionName.value
          );
        })
      : null;

    if (session) {
      const status =
        session.status || session.state || session.connectionStatus || '';
      connectionStatus.value = status.toString().toLowerCase();
    } else {
      connectionStatus.value = 'close';
    }
  } catch (error) {
    connectionStatus.value = 'close';
  }
};

const sleep = ms =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

const waitForQrReady = async (attempts = 8, delayMs = 2000) => {
  const attemptFetch = async remainingAttempts => {
    if (remainingAttempts <= 0) {
      return false;
    }

    if (remainingAttempts < attempts) {
      await sleep(delayMs);
    }

    await loadConnectionStatus();
    const status = connectionStatus.value || '';

    if (qrScanStatuses.has(status)) {
      return true;
    }

    if (connectedStatuses.has(status)) {
      return false;
    }

    return attemptFetch(remainingAttempts - 1);
  };

  return attemptFetch(attempts);
};

const matchesInbox = payload => {
  const incoming = payload.inbox_id ?? payload.inboxId;
  if (incoming !== undefined && incoming !== null) {
    return String(incoming) === String(inbox.value.id);
  }

  const metaId = payload.metadata?.['chatwoot.inbox_id'];
  if (metaId !== undefined && metaId !== null) {
    return String(metaId) === String(inbox.value.id);
  }

  return false;
};

const onQrCodeUpdated = async payload => {
  if (!matchesInbox(payload)) return;
  qrCode.value = await normalizeQrPayload(payload.qr_code);
};

const onConnectionUpdate = payload => {
  if (!matchesInbox(payload)) return;
  connectionStatus.value = (payload.status || '').toLowerCase();
  if (!qrScanStatuses.has(connectionStatus.value)) {
    qrCode.value = null;
    loadConnectionStatus();
  }
};

const generateQRCode = async () => {
  try {
    if (!sessionName.value) {
      useAlert(t('INBOX_MGMT.ADD.WAHA.QR_CODE.NO_QR'));
      return;
    }

    isGeneratingQR.value = true;

    await loadConnectionStatus();
    const currentStatus = connectionStatus.value || '';
    if (currentStatus === 'failed') {
      try {
        await WahaAPI.restartSession(
          providerConfig.value.api_url,
          providerConfig.value.admin_token,
          sessionName.value
        );
      } catch (restartError) {
        useAlert(
          restartError.message || t('INBOX_MGMT.ADD.WAHA.RECREATE.ERROR')
        );
        isGeneratingQR.value = false;
        return;
      }

      const readyAfterRestart = await waitForQrReady();
      if (!readyAfterRestart) {
        useAlert(t('INBOX_MGMT.ADD.WAHA.QR_CODE.NO_QR'));
        isGeneratingQR.value = false;
        return;
      }
    }

    const payload = await WahaAPI.fetchQrCode(
      providerConfig.value.api_url,
      providerConfig.value.admin_token,
      sessionName.value
    );
    const qr = await normalizeQrPayload(payload);
    if (qr && qrScanStatuses.has(connectionStatus.value || '')) {
      qrCode.value = qr;
    } else {
      useAlert(t('INBOX_MGMT.ADD.WAHA.QR_CODE.NO_QR'));
    }
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.ADD.WAHA.QR_CODE.ERROR'));
  } finally {
    isGeneratingQR.value = false;
  }
};

const disconnectSession = async () => {
  try {
    if (!sessionName.value) {
      return;
    }

    isSessionAction.value = true;
    const apiUrl = providerConfig.value.api_url;
    const adminToken = providerConfig.value.admin_token;

    try {
      await WahaAPI.stopSession(apiUrl, adminToken, sessionName.value);
    } catch (stopError) {
      await WahaAPI.deleteSession(apiUrl, adminToken, sessionName.value);
    }

    connectionStatus.value = 'close';
    qrCode.value = null;
    await loadConnectionStatus();
    useAlert(t('INBOX_MGMT.ADD.WAHA.DISCONNECT.SUCCESS'));
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.ADD.WAHA.DISCONNECT.ERROR'));
  } finally {
    isSessionAction.value = false;
  }
};

const recreateSession = async () => {
  try {
    isRecreating.value = true;
    try {
      const config = providerConfig.value;
      if (config.api_url && config.admin_token && sessionName.value) {
        await WahaAPI.logoutSession(
          config.api_url,
          config.admin_token,
          sessionName.value
        );
      }
    } catch (logoutError) {
      // eslint-disable-next-line no-console
      console.warn('[WAHA] Logout session failed', logoutError);
    }

    const response = await InboxesAPI.recreateWahaInstance(inbox.value.id);
    const qr = await normalizeQrPayload(response.data?.qrcode);
    if (qr && qrScanStatuses.has(connectionStatus.value || '')) {
      qrCode.value = qr;
    }
    await loadConnectionStatus();
    useAlert(t('INBOX_MGMT.ADD.WAHA.RECREATE.SUCCESS'));
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.ADD.WAHA.RECREATE.ERROR'));
  } finally {
    isRecreating.value = false;
  }
};

// Executa o fluxo de passkey: busca o desafio no WAHA, assina na extensão
// (aba web.whatsapp.com) e envia a asserção. Trata o código de confirmação
// manual (caso raro). Se a extensão não estiver instalada, a UI mostra o
// botão de instalar (esta função só sai cedo).
const handlePasskey = async () => {
  if (passkeyState.value === 'signing') return;
  const config = providerConfig.value;
  if (!config.api_url || !config.admin_token || !sessionName.value) return;

  passkeyExtension.value = await detectPasskeyExtension();
  if (!passkeyExtension.value.available) return;

  passkeyState.value = 'signing';
  passkeyError.value = '';
  try {
    const challenge = await WahaAPI.getPasskeyChallenge(
      config.api_url,
      config.admin_token,
      sessionName.value
    );
    if (!challenge) {
      passkeyState.value = 'idle';
      return;
    }
    const assertion = await signPasskeyChallenge(
      passkeyExtension.value,
      challenge
    );
    await WahaAPI.submitPasskey(
      config.api_url,
      config.admin_token,
      sessionName.value,
      assertion
    );

    const confirmation = await WahaAPI.getPasskeyConfirmation(
      config.api_url,
      config.admin_token,
      sessionName.value
    );
    if (confirmation?.code) {
      useAlert(
        t('INBOX_MGMT.ADD.WAHA.PASSKEY.CONFIRM_CODE', {
          code: confirmation.code,
        })
      );
      await WahaAPI.confirmPasskey(
        config.api_url,
        config.admin_token,
        sessionName.value
      );
    }
    passkeyState.value = 'done';
    loadConnectionStatus();
  } catch (error) {
    passkeyState.value = 'error';
    passkeyError.value =
      error.message || t('INBOX_MGMT.ADD.WAHA.PASSKEY.ERROR');
  }
};

onMounted(async () => {
  loadConnectionStatus();
  emitter.on(BUS_EVENTS.WHATSAPP_QRCODE_UPDATED, onQrCodeUpdated);
  emitter.on(BUS_EVENTS.WHATSAPP_CONNECTION_UPDATE, onConnectionUpdate);
  passkeyExtension.value = await detectPasskeyExtension();
});

watch(connectionStatus, status => {
  if (!qrScanStatuses.has(status)) {
    qrCode.value = null;
  }
  // Quando o WhatsApp pede passkey e a extensão está pronta, assina na hora.
  if (
    passkeyRequiredStatuses.has(status) &&
    passkeyExtension.value.available &&
    passkeyState.value !== 'signing'
  ) {
    handlePasskey();
  }
});

onUnmounted(() => {
  emitter.off(BUS_EVENTS.WHATSAPP_QRCODE_UPDATED, onQrCodeUpdated);
  emitter.off(BUS_EVENTS.WHATSAPP_CONNECTION_UPDATE, onConnectionUpdate);
});
</script>

<template>
  <div class="flex flex-col items-center justify-center py-8">
    <!-- Connection Status -->
    <div class="flex flex-col items-center gap-4 mb-6">
      <div
        class="flex items-center gap-2 px-4 py-2 rounded-full"
        :class="{
          'bg-green-100 dark:bg-green-900/30': isConnected,
          'bg-yellow-100 dark:bg-yellow-900/30': [
            'connecting',
            'starting',
          ].includes(connectionStatus),
          'bg-red-100 dark:bg-red-900/30':
            !isConnected &&
            !['connecting', 'starting'].includes(connectionStatus),
        }"
      >
        <span
          class="w-3 h-3 rounded-full"
          :class="{
            'bg-green-500': isConnected,
            'bg-yellow-500 animate-pulse': ['connecting', 'starting'].includes(
              connectionStatus
            ),
            'bg-red-500':
              !isConnected &&
              !['connecting', 'starting'].includes(connectionStatus),
          }"
        />
        <span
          class="font-medium"
          :class="{
            'text-green-700 dark:text-green-300': isConnected,
            'text-yellow-700 dark:text-yellow-300': [
              'connecting',
              'starting',
            ].includes(connectionStatus),
            'text-red-700 dark:text-red-300':
              !isConnected &&
              !['connecting', 'starting'].includes(connectionStatus),
          }"
        >
          {{ statusText }}
        </span>
      </div>
    </div>

    <!-- QR Code Display (when not connected) -->
    <div v-if="!isConnected" class="flex flex-col items-center gap-4">
      <!-- Passkey: quando o WhatsApp pede passkey ao parear (além do QR) -->
      <div
        v-if="isPasskeyRequired"
        class="w-full max-w-sm p-4 border rounded-xl border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800"
      >
        <p class="mb-2 font-medium text-slate-800 dark:text-slate-100">
          {{ $t('INBOX_MGMT.ADD.WAHA.PASSKEY.TITLE') }}
        </p>

        <p
          v-if="passkeyState === 'signing'"
          class="text-sm text-slate-600 dark:text-slate-400"
        >
          {{ $t('INBOX_MGMT.ADD.WAHA.PASSKEY.SIGNING') }}
        </p>

        <div v-else-if="!passkeyExtension.available">
          <p class="mb-3 text-sm text-slate-600 dark:text-slate-400">
            {{ $t('INBOX_MGMT.ADD.WAHA.PASSKEY.INSTALL_HINT') }}
          </p>
          <div class="flex flex-wrap items-center gap-2">
            <NextButton
              :label="$t('INBOX_MGMT.ADD.WAHA.PASSKEY.INSTALL_BUTTON')"
              icon="i-lucide-puzzle"
              @click="openInstall"
            />
            <NextButton
              variant="faded"
              :label="$t('INBOX_MGMT.ADD.WAHA.PASSKEY.ALREADY_INSTALLED')"
              @click="handlePasskey"
            />
          </div>
        </div>

        <div v-else-if="passkeyState === 'error'">
          <p class="mb-2 text-sm text-red-600 dark:text-red-400">
            {{ passkeyError }}
          </p>
          <NextButton
            :label="$t('INBOX_MGMT.ADD.WAHA.PASSKEY.RETRY')"
            @click="handlePasskey"
          />
        </div>

        <NextButton
          v-else
          :label="$t('INBOX_MGMT.ADD.WAHA.PASSKEY.CONFIRM_BUTTON')"
          icon="i-lucide-key-round"
          @click="handlePasskey"
        />
      </div>

      <!-- QR Code Image -->
      <div
        v-if="qrCode"
        class="p-4 bg-white rounded-xl shadow-lg border border-slate-200 dark:border-slate-700"
      >
        <img
          :src="qrCode"
          alt="WhatsApp QR Code"
          class="w-64 h-64 object-contain"
        />
      </div>

      <!-- Placeholder when no QR -->
      <div
        v-else
        class="w-64 h-64 flex flex-col items-center justify-center bg-slate-100 dark:bg-slate-800 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600"
      >
        <fluent-icon icon="search" size="48" class="text-slate-400 mb-2" />
        <p class="text-sm text-slate-500 dark:text-slate-400 text-center px-4">
          {{ $t('INBOX_MGMT.ADD.WAHA.QR_CODE.CLICK_TO_GENERATE') }}
        </p>
      </div>

      <!-- Instructions -->
      <p
        class="text-sm text-slate-600 dark:text-slate-400 text-center max-w-xs"
      >
        {{ $t('INBOX_MGMT.ADD.WAHA.QR_CODE.INSTRUCTIONS') }}
      </p>

      <!-- Action Buttons -->
      <div class="flex gap-3 mt-2">
        <NextButton
          v-if="canShowQRCode"
          :label="$t('INBOX_MGMT.ADD.WAHA.QR_CODE.GENERATE')"
          :is-loading="isGeneratingQR"
          @click="generateQRCode"
        />
        <NextButton
          variant="faded"
          :label="$t('INBOX_MGMT.ADD.WAHA.RECREATE.BUTTON')"
          :is-loading="isRecreating"
          @click="recreateSession"
        />
      </div>
    </div>

    <!-- Connected State -->
    <div v-else class="flex flex-col items-center gap-4">
      <div
        class="w-64 h-64 flex flex-col items-center justify-center bg-green-50 dark:bg-green-900/20 rounded-xl border-2 border-green-200 dark:border-green-800"
      >
        <fluent-icon
          icon="checkmark-circle"
          size="64"
          class="text-green-500 mb-2"
        />
        <p class="text-lg font-medium text-green-700 dark:text-green-300">
          {{ $t('INBOX_MGMT.ADD.WAHA.STATUS.CONNECTED') }}
        </p>
        <p
          class="text-sm text-green-600 dark:text-green-400 text-center px-4 mt-1"
        >
          {{ $t('INBOX_MGMT.ADD.WAHA.STATUS.READY') }}
        </p>
      </div>

      <!-- Disconnect Button -->
      <NextButton
        variant="faded"
        color="ruby"
        :label="$t('INBOX_MGMT.ADD.WAHA.DISCONNECT.BUTTON')"
        :is-loading="isSessionAction"
        @click="disconnectSession"
      />
    </div>
  </div>
</template>
